import api from './api';

export interface WarehouseStockRow {
  id: number;
  warehouse_code: string;
  project_id: string | null;
  project_name: string | null;
  item_name: string;
  unit: string;
  quantity: number;
  avg_unit_price: number | null;
  total_value: number | null;
  last_updated: string;
}

export interface WarehouseDocumentRow {
  id: number;
  document_number: string;
  document_type: 'PZ' | 'WZ' | 'WW' | 'MM';
  document_date: string;
  project_id: string | null;
  project_name: string | null;
  warehouse_code: string;
  target_warehouse_code: string | null;
  contractor_name: string | null;
  source_invoice_id: number | null;
  description: string | null;
  status: 'Otwarty' | 'Zamknięty' | 'Anulowany';
  creator_first_name: string | null;
  creator_last_name: string | null;
  items_count: number;
  total_net: number;
  total_gross: number;
  created_at: string;
  updated_at: string;
}

export interface WarehouseDocumentItem {
  id: number;
  document_id: number;
  ordinal: number;
  name: string;
  quantity: number;
  unit: string;
  unit_price: number | null;
  net_value: number | null;
  vat_rate: string;
  gross_value: number | null;
  source_invoice_item_id: number | null;
}

export interface WarehouseDocumentDetail extends WarehouseDocumentRow {
  items: WarehouseDocumentItem[];
}

export interface Warehouse {
  id: number;
  warehouse_code: string;
  warehouse_name: string;
  project_id: string | null;
  project_name?: string | null;
  ordering_party: string | null;
  address: string | null;
}

export async function listWarehouses(): Promise<Warehouse[]> {
  const { data } = await api.get('/warehouse/warehouses');
  return data.data || [];
}

export async function createWarehouse(payload: { warehouse_code: string; warehouse_name: string; project_id?: string }): Promise<Warehouse> {
  const { data } = await api.post('/warehouse/warehouses', payload);
  return data.data;
}

export async function addInvoiceToWarehouse(invoiceId: number): Promise<{ document_number: string; id: number }> {
  const { data } = await api.post(`/warehouse/from-invoice/${invoiceId}`);
  return data.data;
}

export interface StockFilters {
  search?: string;
  project_id?: string;
  warehouse_code?: string;
  month?: string;
  date_from?: string;
  date_to?: string;
}

export interface DocumentFilters extends StockFilters {
  document_type?: string;
}

export async function listWarehouseStock(filters: StockFilters = {}): Promise<WarehouseStockRow[]> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
  const { data } = await api.get(`/warehouse/stock?${params.toString()}`);
  return data.data || [];
}

export async function listWarehouseDocuments(filters: DocumentFilters = {}): Promise<WarehouseDocumentRow[]> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
  const { data } = await api.get(`/warehouse/documents?${params.toString()}`);
  return data.data || [];
}

export async function getWarehouseDocument(id: number): Promise<WarehouseDocumentDetail> {
  const { data } = await api.get(`/warehouse/documents/${id}`);
  return data.data;
}

export interface CreateDocumentPayload {
  document_type: 'WZ' | 'WW' | 'MM';
  document_date: string;
  project_id?: string;
  warehouse_code: string;
  target_warehouse_code?: string;
  contractor_name?: string;
  contractor_id?: number;
  description?: string;
  items: Array<{
    name: string;
    quantity: number;
    unit: string;
    unit_price?: number;
    net_value?: number;
    vat_rate?: string;
    gross_value?: number;
  }>;
}

export async function createWarehouseDocument(payload: CreateDocumentPayload): Promise<{ id: number; document_number: string }> {
  const { data } = await api.post('/warehouse/documents', payload);
  return data.data;
}
